"use client";

import type { AddonDietAddonsRead } from "@repo/api/catalog/api/schemas/addon-diet-addons-read";
import type { DietAddonGroupDietAddonsRead } from "@repo/api/catalog/api/schemas/diet-addon-group-diet-addons-read";
import { useMemo, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { useAddonViewItemListEvent } from "@/features/analytics/hooks/use-addon-view-item-list-event";
import { useCarouselScrollbar } from "@/lib/hooks/use-carousel-scrollbar";
import { AddonCategoryTabs } from "./addon-category-tabs";
import { DietAddonCard } from "./diet-addon-card";

const byPosition = <T extends { position: number }>(a: T, b: T) =>
  a.position - b.position;

type DietAddonsCarouselProps = {
  categories: DietAddonGroupDietAddonsRead[];
  daysCount?: number;
  onAddAddonAction?: (addon: AddonDietAddonsRead) => void;
  cateringName?: string;
};

export const DietAddonsCarousel = ({
  categories,
  onAddAddonAction,
  cateringName,
}: DietAddonsCarouselProps) => {
  const {
    count,
    handleScrollbarPointerCancel,
    handleScrollbarPointerDown,
    handleScrollbarPointerMove,
    handleScrollbarPointerUp,
    isDragging,
    scrollbarThumbOffset,
    scrollbarThumbWidth,
    scrollbarTrackRef,
    setApi,
  } = useCarouselScrollbar();

  const sortedCategories = useMemo(
    () => [...categories].sort(byPosition),
    [categories],
  );

  const [activeCategory, setActiveCategory] = useState<string>(
    sortedCategories.at(0)?.slug ?? "",
  );

  const active = useMemo(
    () => sortedCategories.find((item) => item.slug === activeCategory),
    [sortedCategories, activeCategory],
  );

  const activeAddons = useMemo(
    () =>
      (active?.addons ?? []).filter((addon) => addon.active).sort(byPosition),
    [active],
  );

  useAddonViewItemListEvent({
    addons: activeAddons,
    cateringName,
    categoryName: active?.name,
    enabled: true,
    listType: "addon-config",
  });

  return (
    <div className="min-w-0 space-y-8">
      {/* Category tabs scroll horizontally on their own; only the cards below
          live inside the embla carousel. */}
      <AddonCategoryTabs
        activeCategory={activeCategory}
        categories={sortedCategories}
        fadeClassName="to-brand-grey1"
        onSelectCategory={setActiveCategory}
      />

      {/* Only the addon cards live inside the carousel. */}
      <div className="min-w-0">
        <Carousel
          className="relative"
          key={activeCategory}
          // No dragFree: snap to discrete positions so trimSnaps clamps the last
          // snap exactly to the end (otherwise the arrow can over-scroll a few px).
          opts={{ align: "start", containScroll: "trimSnaps" }}
          setApi={setApi}
        >
          <CarouselContent className="gap-4 pl-4">
            {activeAddons.map((addon, position) => (
              <CarouselItem className="basis-42.5 pl-0" key={addon.id}>
                <DietAddonCard
                  addon={addon}
                  cateringName={cateringName}
                  categoryName={active?.name}
                  index={position + 1}
                  listType="addon-config"
                  onAddAddonAction={onAddAddonAction}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
          {!isDragging && (
            <>
              {/* Desktop only — the mobile design scrolls the cards by swipe
                  and shows just the progress bar underneath. */}
              <CarouselNext
                className="right-2 h-14 w-14 rounded-xl border border-brand-grey2 bg-brand-white text-brand-green2-300 hover:bg-white/90 disabled:hidden max-md:hidden"
                type="button"
              />
              <CarouselPrevious
                className="left-2 h-14 w-14 rounded-xl border border-brand-grey2 bg-brand-white text-brand-green2-300 hover:bg-white/90 disabled:hidden max-md:hidden"
                type="button"
              />
            </>
          )}
        </Carousel>

        {count > 1 && (
          <div
            className="relative mt-6 h-1 w-full cursor-pointer touch-none select-none rounded-full bg-brand-grey2"
            onPointerCancel={handleScrollbarPointerCancel}
            onPointerDown={handleScrollbarPointerDown}
            onPointerMove={handleScrollbarPointerMove}
            onPointerUp={handleScrollbarPointerUp}
            ref={scrollbarTrackRef}
          >
            <div
              className="absolute top-0 h-full rounded-full bg-brand-green2-300 transition-all"
              style={{
                left: `${scrollbarThumbOffset}%`,
                width: `${scrollbarThumbWidth}%`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
